// src/PrivacyPolicy.js
import React, { useState } from 'react';
import './PrivacyPolicy.css';

/*
 * The last-updated date is a constant, not new Date().
 *
 * Both legal pages used to render today's date, so the document claimed it had
 * been revised on whatever day you happened to read it. That defeats the
 * purpose of the field, and on something people rely on for their rights it is
 * a false statement of fact.
 *
 * This date is the last commit that changed the wording (d96defa, 20 Feb
 * 2026). It needs confirming against whatever legal counsel treats as the true
 * last revision, and updating by hand whenever the text below changes.
 */
const LAST_UPDATED = '20 February 2026';

export default function PrivacyPolicy() {
    const [expandedSections, setExpandedSections] = useState({});

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const expandAll = () => {
        const allSections = {};
        for (let i = 1; i <= 12; i++) {
            allSections[`section${i}`] = true;
        }
        setExpandedSections(allSections);
    };

    const collapseAll = () => {
        setExpandedSections({});
    };

    const sectionColors = [
        '#104378', '#0d8a72', '#8b5cf6', '#f59e0b', '#ef4444',
        '#ec4899', '#6366f1', '#14b8a6', '#06b6d4', '#f97316',
        '#10b981', '#64748b'
    ];

    return (
        <div className="privacy-page">
            {/* Hero on the site baseline. .pa2 is scoped to the hero deliberately:
                .pa2 a sets colour:inherit and removes underlines, which would make
                every inline link and mailto in the notice below unreadable as a link. */}
            <div className="pa2">
                <section className="hero" id="top">
                    <div className="wrap">
                        <h1 className="on-dark">
                            <span className="setup">Your data,</span>
                            <span className="payoff">and what we do with it.</span>
                        </h1>
                        <p className="lede">
                            At PhysioActivity, we respect your privacy and are committed to protecting your
                            personal and health data. This privacy statement explains what personal data we
                            collect, how we use it, and your rights regarding your information.
                        </p>
                    </div>
                </section>
            </div>

            <main className="privacy-main">

                {/* Expand/Collapse All Controls */}
                <div className="privacy-controls">
                    <button onClick={expandAll} className="privacy-control-btn">Expand All</button>
                    <button onClick={collapseAll} className="privacy-control-btn">Collapse All</button>
                </div>

                {/* Content Sections */}
                <div className="privacy-sections">

                    {/* Section 1: Personal Data We Collect */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section1')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[0]}}>1</div>
                                <h2 className="privacy-section-title">Personal Data We Collect</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section1'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section1'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity collects data to provide you with effective physiotherapy services and improve your musculoskeletal health. You provide some of this data directly, such as when you create an account, schedule an appointment, or communicate with your physiotherapist. We get some of it by recording how you interact with our services, such as using technologies like cookies.
                                </p>

                                <h3 className="privacy-subsection-title">Data you provide:</h3>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Name and contact data.</span> Your first and last name, email address, postal address, phone number, and other similar contact information.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Credentials.</span> Passwords, password hints, and similar security information used for authentication and account access.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Demographic data.</span> Data about you such as your age, gender, country, and preferred language.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Payment data.</span> Data to process payments, such as your payment instrument number and the security code associated with your payment instrument.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Health data.</span> With your explicit consent, we collect information about your musculoskeletal condition, injury history, pain levels, treatment goals, progress metrics, movement patterns, and data from wearable devices. This includes video recordings of your movement during assessments.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Content.</span> Content of your files and communications you input, upload, or send through PhysioActivity services, such as messages to your physiotherapist, photographs of affected areas, or exercise videos you submit for form correction.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Video and voice data.</span> Recordings of video consultations and voice communications when you use our telehealth services.
                                        </p>
                                    </li>
                                </ul>

                                <h3 className="privacy-subsection-title">Data we collect automatically:</h3>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Device and usage data.</span> Data about your device and how you and your device interact with PhysioActivity, including IP address, browser type, operating system, referring/exit pages, and date/time stamps.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Location data.</span> Data about your location, which can be precise or imprecise, to provide location-based services such as finding physiotherapists near you.
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 2: How We Use Personal Data */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section2')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[1]}}>2</div>
                                <h2 className="privacy-section-title">How We Use Personal Data</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section2'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section2'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity uses the data we collect to provide you with rich, interactive physiotherapy experiences. In particular, we use data to:
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Provide our physiotherapy services.</span> We use data to operate our services, including assessments, treatment planning, exercise prescription, and progress monitoring.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Improve and develop our services.</span> We use data to continually improve our services, including adding new features and capabilities.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Personalise your experience.</span> We use data to provide personalised recommendations, content, and exercise programmes tailored to your specific needs and condition.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Communicate with you.</span> We use data to respond to your questions, send service notifications, and provide important account information.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Promote safety and security.</span> We use data to protect the security and safety of our services and our patients, including detecting fraud and abuse.
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 3: Reasons We Share Personal Data */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section3')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[2]}}>3</div>
                                <h2 className="privacy-section-title">Reasons We Share Personal Data</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section3'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section3'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    We share your personal data with your consent or as necessary to provide services you have requested or authorized. We also share data with physiotherapists and healthcare providers as needed to deliver your treatment.
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Healthcare providers.</span> We share relevant health data with your assigned physiotherapist and, with your consent, other healthcare providers involved in your care.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Service providers.</span> We share data with vendors working on our behalf for purposes such as payment processing, data hosting, and customer support.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Legal requirements.</span> We may disclose data in response to legal process or to protect our rights, comply with law enforcement, or in cases of emergency to protect health and safety.
                                        </p>
                                    </li>
                                </ul>
                                <div className="privacy-warning-box">
                                    <p className="privacy-warning-text">
                                        <span className="privacy-warning-label">Important:</span> We will never sell your personal or health data to third parties. We do not share your health data for advertising purposes.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Section 4: How to Access and Control Your Personal Data */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section4')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[3]}}>4</div>
                                <h2 className="privacy-section-title">How to Access and Control Your Personal Data</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section4'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section4'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    You can access and control your personal data that PhysioActivity has obtained. You have several choices regarding how we collect, use, and share your data.
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Data access.</span> You can access your personal data in your account dashboard or by contacting us at privacy@physioactivity.com.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Data portability.</span> You can request a copy of your personal data in a structured, machine-readable format.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Data correction.</span> You can update and correct your personal data through your account settings.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Data deletion.</span> You can request deletion of your personal data. Note that we may retain certain data as required by law or for legitimate business purposes.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Communication preferences.</span> You can manage your communication preferences and opt out of marketing emails through your account settings.
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 5: Cookies and Similar Technologies */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section5')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[4]}}>5</div>
                                <h2 className="privacy-section-title">Cookies and Similar Technologies</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section5'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section5'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    We use cookies (small text files placed on your device) and similar technologies to provide our services and help collect data. Cookies allow us to store your preferences and settings, enable you to sign in, provide interest-based advertising, combat fraud, and analyse how our services are performing.
                                </p>
                                <h3 className="privacy-subsection-title">We use cookies to:</h3>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">Keep you signed in</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">Remember your preferences and settings</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">Provide personalised content and experiences</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">Understand how you use our services</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">Improve security and detect fraud or abuse</p>
                                    </li>
                                </ul>
                                <p className="privacy-section-text">
                                    You have a variety of tools to control cookies and similar technologies, including browser controls to block and delete cookies and controls from some analytics service providers.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Section 6: Children's Data */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section6')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[5]}}>6</div>
                                <h2 className="privacy-section-title">Children's Data</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section6'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section6'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    When PhysioActivity collects data from children under the age of 18, we obtain verifiable parental or guardian consent as required by applicable law. Parents or guardians can review and request deletion of the personal data PhysioActivity has collected from their child.
                                </p>
                                <p className="privacy-section-text">
                                    If you are a parent or guardian and believe that we have collected information from your child without appropriate consent, please contact us at privacy@physioactivity.com.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Section 7: Data Security */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section7')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[6]}}>7</div>
                                <h2 className="privacy-section-title">Data Security</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section7'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section7'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity is committed to protecting the security of your personal data. We use a variety of security technologies and procedures to help protect your personal data from unauthorized access, use, or disclosure.
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Encryption.</span> We encrypt your data in transit using TLS and at rest using industry-standard encryption methods.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Access controls.</span> We restrict access to personal data to PhysioActivity employees, contractors, and agents who need access to provide, operate, or improve our services.
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Regular audits.</span> We regularly review our security practices and conduct security assessments to identify and address potential vulnerabilities.
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 8: Data Retention */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section8')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[7]}}>8</div>
                                <h2 className="privacy-section-title">Data Retention</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section8'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section8'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity retains personal data for as long as necessary to provide the services and fulfil the purposes outlined in this privacy statement. We also retain data as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
                                </p>
                                <p className="privacy-section-text">
                                    Health-related data may be retained for longer periods as required by healthcare regulations and professional standards. When we no longer need your data, we will delete or anonymise it.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Section 9: International Data Transfers */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section9')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[8]}}>9</div>
                                <h2 className="privacy-section-title">International Data Transfers</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section9'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section9'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity operates globally and may transfer your personal data to countries other than the one in which you live. We ensure that all transfers comply with applicable data protection laws and that appropriate safeguards are in place.
                                </p>
                                <p className="privacy-section-text">
                                    When we transfer personal data from the European Economic Area and the UK to other countries, we use approved transfer mechanisms, including Standard Contractual Clauses and adequacy decisions.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Section 10: Your Legal Rights */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section10')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[9]}}>10</div>
                                <h2 className="privacy-section-title">Your Legal Rights</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section10'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section10'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    Subject to applicable law, you have the following rights regarding your personal data:
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to access</span> your personal data</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to rectification</span> of inaccurate personal data</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to erasure</span> (right to be forgotten)</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to restriction</span> of processing</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to data portability</span></p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to object</span> to processing</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to withdraw consent</span> at any time</p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text"><span className="privacy-list-label">Right to lodge a complaint</span> with a supervisory authority</p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 11: Compliance and Regulation */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section11')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[10]}}>11</div>
                                <h2 className="privacy-section-title">Compliance and Regulation</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section11'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section11'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    PhysioActivity complies with applicable data protection and privacy laws, including:
                                </p>
                                <ul className="privacy-section-list">
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">General Data Protection Regulation (GDPR)</span> - for individuals in the European Economic Area and UK
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">UK Data Protection Act 2018</span>
                                        </p>
                                    </li>
                                    <li className="privacy-section-list-item">
                                        <div className="privacy-check-icon">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className="privacy-list-text">
                                            <span className="privacy-list-label">Healthcare professional standards</span> for patient data protection
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Section 12: Contact Us */}
                    <div className="privacy-section">
                        <button onClick={() => toggleSection('section12')} className="privacy-section-header">
                            <div className="privacy-section-header-content">
                                <div className="privacy-section-number" style={{background: sectionColors[11]}}>12</div>
                                <h2 className="privacy-section-title">How to Contact Us</h2>
                            </div>
                            <svg className={`privacy-chevron ${expandedSections['section12'] ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {expandedSections['section12'] && (
                            <div className="privacy-section-content">
                                <p className="privacy-section-text">
                                    If you have a privacy concern, complaint, or question, please contact us. We will respond to questions or concerns within 30 days.
                                </p>
                                <div className="privacy-contact-box">
                                    <h3 className="privacy-contact-title">Privacy Contact Information:</h3>
                                    <div className="privacy-contact-info">
                                        <p><span className="privacy-contact-label">Email:</span> privacy@physioactivity.com</p>
                                        <p><span className="privacy-contact-label">Data Protection Officer:</span> dpo@physioactivity.com</p>
                                        <p className="privacy-contact-note">
                                            If you are not satisfied with our response, you have the right to lodge a complaint with your local data protection authority.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>

                {/* Changes to This Privacy Statement */}
                <div className="privacy-changes">
                    <h2 className="privacy-changes-title">Changes to This Privacy Statement</h2>
                    <p className="privacy-changes-text">
                        We may update this privacy statement from time to time. When we update the statement, we will revise the "Last updated" date at the end of this statement and describe the changes in our update history. If there are material changes to the statement or in how PhysioActivity will use your personal data, we will notify you either by prominently posting a notice of such changes before they take effect or by directly sending you a notification.
                    </p>
                </div>

                {/* Last Updated Badge */}
                <div className="privacy-last-updated">
                    <div className="privacy-badge">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="privacy-badge-text">
                            This Privacy Statement was last updated on {LAST_UPDATED}
                        </span>
                    </div>
                </div>
            </main>
        </div>
    );
}
